// ============================================================
// PERRY — THE EMBER CONSTELLATION
// A small tree on purpose. A beb's Ascension should feel like an
// event, not a shopping trip. Twelve nodes, single-digit costs,
// and one node that is FREE and will cost you forever.
//
// THE FAUSTIAN FORK: "Deviant Ember" hands over +2 Lyt for
// nothing, and locks your Max Chao where it stands — the Growth
// Law never speaks to you again. It is offered honestly, in full,
// with the price written on the front. Some players will take it
// anyway. That is the point.
//
// Points are DERIVED: floor(Renown / 20) − spent. Nothing about
// "level" is ever stored.
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, ensureKeyframes } from "./PerryUI";
import {
  NODES, RULES, SPECIES, UHLTI, REGALIA, GLYPH,
  TREASURES, TREASURE_ORDER, FINISHES, FINISH_ORDER,
  availablePoints, spentPoints, deriveAnimaCap, deriveMaxChao,
  deriveLyt, deriveGrace, deriveStanding, ownsTreasure,
  // ── 2026-07-29 ── the rank ladder and the breath bar
  TRACKS, TRACK_ORDER, deriveTrack, deriveLyf, deriveExhaustMs, STAT_NOTES,
  trackLevel as trackLevelOf,
  deriveTreasure, deriveFinish, treasureLine,
  deriveRegalia, deriveSlots, deriveBenched, slotKey, themeFor,
} from "./perryData";
import Outfitter from "./Outfitter";

const KF = `
@keyframes cnIn    { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes cnPulse { 0%,100% { r: 3.4; opacity: 0.5; } 50% { r: 5.2; opacity: 1; } }
@keyframes cnWarn  { 0%,100% { border-color: #8e3a30; } 50% { border-color: #f4726a; } }
`;

export default function Customize({ soul, crests = 0, kindling = 0, onTake, onAscend, onEquip, onFinish, onSlots, onBuy, onAscension, onBack }) {
  ensureKeyframes("perry-constellation-keyframes", KF);
  // ── THREE HALVES OF GETTING STRONGER, 2026-07-27 ──
  // What you know (stars), what you are holding (armoury), and
  // what you can still go and buy (wares). The third one used to
  // be reachable only by surviving to Hollowmere, which meant the
  // player who most needed to spend a coin was the only one who
  // could not. Now every ⋔ the road pays you has somewhere to go
  // the moment you earn it.
  // ── FOUR HALVES NOW, 2026-07-28 ──
  // What you KNOW (stars), what you CARRY (grimoire), what you
  // HOLD (armoury), and what you can still go and BUY (wares).
  // The Grimoire is new and it is the one this pass exists for:
  // ten powers, five keys, and the gap between those two numbers
  // is the entire feature.
  const [tab, setTab] = useState("stars");     // "stars" | "grimoire" | "armoury" | "wares"
  const [sel, setSel] = useState(null);
  const [confirmDeviant, setConfirmDeviant] = useState(false);
  const points = availablePoints(soul);
  const canBuyPoint = kindling >= RULES.ASCEND_COST;
  const [buying, setBuying] = useState(false);
  // which stat row is open, and which track row is open. Two
  // separate pieces of state so opening one never closes the other
  // — a player comparing Lyf against Sharpened Answer wants both.
  const [openStat, setOpenStat] = useState(null);
  const [openTrack, setOpenTrack] = useState(null);
  const taken = soul?.nodes || {};
  const sp = SPECIES[soul?.species];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !e.repeat) onBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const reqMet = (n) => (n.req || []).every((r) => taken[r]);
  const status = (n) => {
    if (taken[n.id]) return "taken";
    if (n.locked) return "sealed";
    if (!reqMet(n)) return "unreachable";
    if (n.deviant && soul?.deviant) return "unreachable";
    if (points < n.cost) return "unaffordable";
    return "open";
  };

  const take = (n) => {
    if (status(n) !== "open") return;
    if (n.deviant && !confirmDeviant) { setConfirmDeviant(true); return; }
    setConfirmDeviant(false);
    onTake(n);
  };

  const COLORS = {
    taken: GM.amethystBright,
    open: GM.inkGoldBright,
    unaffordable: GM.inkGoldDim,
    unreachable: "#3a2a20",
    sealed: "#4a3a52",
  };

  const SKY = NODES.filter((n) => !n.shelved);
  const selNode = sel ? NODES.find((n) => n.id === sel) : null;
  const selStatus = selNode ? status(selNode) : null;

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      overflow: "auto", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={16} />

      <div style={{ position: "relative", padding: "30px 20px 50px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: "clamp(24px, 6vw, 36px)", letterSpacing: 9,
            color: GM.vellum, textShadow: `0 0 34px ${GM.inkGold}`,
          }}>{tab === "stars" ? "THE EMBER CONSTELLATION"
              : tab === "grimoire" ? "THE GRIMOIRE"
                : tab === "armoury" ? "THE ARMOURY" : "WHYLLIE'S COUNTER"}</div>
          <MetalRule width="min(340px, 74vw)" style={{ margin: "9px auto 8px" }} />
          {/* ── ONE COIN, TWO COUNTERS (2026-07-29) ──
              Points are no longer a thing you HAVE because you
              fought well. They are a thing you BUY, out of the same
              purse the weapon rack draws on — so this button sits
              directly above the Kindling line on purpose. Every
              point is an axe you did not buy, and the screen should
              never let you forget which. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: points > 0 ? GM.amethystBright : GM.vellumFaint }}>
              {points} ASCENSION POINT{points === 1 ? "" : "S"}
            </div>
            <button
              onClick={async () => {
                if (!canBuyPoint || buying) return;
                setBuying(true);
                try { await onAscend?.(1); } finally { setBuying(false); }
              }}
              disabled={!canBuyPoint || buying}
              style={{
                fontFamily: GM.font, fontSize: 10, letterSpacing: 2,
                padding: "5px 11px", borderRadius: 7, cursor: canBuyPoint ? "pointer" : "not-allowed",
                background: canBuyPoint ? "rgba(160,106,192,0.16)" : "transparent",
                border: `1px solid ${canBuyPoint ? GM.amethystBright : GM.pageBorder}`,
                color: canBuyPoint ? GM.amethystBright : GM.vellumFaint,
                opacity: buying ? 0.5 : 1,
              }}
            >
              {buying ? "…" : `ASCEND · ${RULES.ASCEND_COST} ${GLYPH.kd}`}
            </button>
          </div>
          {/* ── TWO PURSES, 2026-07-28 ──
              Kindling is loud because Kindling is what the cart
              takes. Divine Crests sit under it, quieter and gold,
              because there is exactly one thing in Perry they buy
              and a player who has none should not feel poor. */}
          <div style={{ fontSize: 12, letterSpacing: 3, color: GM.inkGoldBright, marginTop: 4 }}>
            {GLYPH.kd} {kindling} KINDLING
          </div>
          <div style={{ fontSize: 10, letterSpacing: 3, color: GM.vellumFaint, marginTop: 3 }}>
            {GLYPH.dc} {crests} DIVINE CRESTS · THE HIGH SHELF
          </div>
        </div>

        {/* ── what you know · what you hold · what you can buy ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", justifyContent: "center" }}>
          {[
            ["stars", "✦ CONSTELLATION"],
            ["grimoire", "◈ GRIMOIRE"],
            ["armoury", "⚔ ARMOURY"],
            ["wares", `${GLYPH.kd} WARES`],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              padding: "7px 16px", borderRadius: 8, cursor: "pointer",
              background: tab === id
                ? "linear-gradient(175deg, #7a4218, #3a2010)"
                : "linear-gradient(175deg, #1c1009, #100906)",
              border: `1px solid ${tab === id ? GM.inkGold : GM.pageBorder}`,
              color: tab === id ? "#ffe6c2" : GM.vellumDim,
              fontFamily: GM.fontUI, fontSize: 11, letterSpacing: 3,
            }}>{label}</button>
          ))}
          {/* ── THE DOOR TO THE SHEATHE, 2026-07-30 ──
              A BUTTON, not a fifth tab, and it looks different on
              purpose. The four tabs beside it are lists of things
              you own; this leaves the screen entirely and goes
              somewhere a character is waiting. Making it look like
              a peer of "ARMOURY" would have undersold the single
              most important room in the game. */}
          {onAscension && (
            <button onClick={onAscension} style={{
              padding: "7px 16px", borderRadius: 8, cursor: "pointer",
              background: "linear-gradient(175deg, #4a3410, #1a1008)",
              border: "1px solid #ffe6a8",
              color: "#ffe6a8",
              fontFamily: GM.fontUI, fontSize: 11, letterSpacing: 3,
            }}>⟡ THE ASCENSION →</button>
          )}
        </div>

        {tab === "grimoire" && (
          <Grimoire soul={soul} onSlots={onSlots} />
        )}

        {tab === "armoury" && (
          <Armoury soul={soul} onEquip={onEquip} onFinish={onFinish} />
        )}

        {tab === "wares" && (
          <div style={{
            width: "min(660px, 94vw)", padding: "18px 22px", borderRadius: 12,
            background: "linear-gradient(175deg, #1c1009, #0d0705)",
            border: `1px solid ${GM.pageBorder}`,
            boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
            animation: "cnIn 0.5s ease-out",
          }}>
            <Outfitter soul={soul} crests={crests} kindling={kindling} onBuy={onBuy} />
          </div>
        )}

        {tab === "stars" && (
        <div style={{
          width: "min(940px, 96vw)", display: "grid", gap: 16,
          gridTemplateColumns: "minmax(300px, 1.35fr) minmax(260px, 1fr)",
        }}>
          {/* ── the sky ── */}
          <div style={{
            position: "relative", aspectRatio: "1 / 0.92", borderRadius: 14,
            background: "radial-gradient(ellipse at 50% 20%, #1a1226 0%, #0c0708 62%, #060404 100%)",
            border: `1px solid ${GM.pageBorder}`, overflow: "hidden",
            boxShadow: "inset 0 0 60px rgba(0,0,0,0.8)",
            animation: "cnIn 0.5s ease-out",
          }}>
            {/* ── SHELVED STARS DO NOT RENDER (2026-07-29) ──
                The Deviant Ember is `shelved: true` — its id lives
                on so any Soul that already took it keeps its flag
                and its history, but it is off the sky and out of
                the shop until the Fork comes back as a design
                conversation rather than a field edit. Filtering
                here (not deleting there) is what makes that
                reversible. */}
            <svg viewBox="0 0 100 92" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              {/* links */}
              {SKY.map((n) =>
                (n.req || []).map((rid) => {
                  const r = NODES.find((x) => x.id === rid);
                  if (!r) return null;
                  const lit = taken[n.id] && taken[rid];
                  const reach = taken[rid];
                  return (
                    <line
                      key={`${n.id}-${rid}`}
                      x1={r.x} y1={r.y * 0.92} x2={n.x} y2={n.y * 0.92}
                      stroke={lit ? GM.amethyst : reach ? GM.inkGoldDim : "#241a14"}
                      strokeWidth={lit ? 0.55 : 0.32}
                      opacity={lit ? 0.9 : 0.55}
                    />
                  );
                })
              )}
              {/* nodes */}
              {SKY.map((n) => {
                const st = status(n);
                const c = COLORS[st];
                const isSel = sel === n.id;
                const r = n.deviant ? 3.1 : n.locked ? 3.4 : 2.6;
                return (
                  <g key={n.id} onClick={() => setSel(n.id)} style={{ cursor: "pointer" }}>
                    {(st === "taken" || st === "open") && (
                      <circle cx={n.x} cy={n.y * 0.92} r={r * 2.4} fill={c} opacity={st === "taken" ? 0.14 : 0.08} />
                    )}
                    <circle
                      cx={n.x} cy={n.y * 0.92} r={r}
                      fill={st === "taken" ? c : "#0a0605"}
                      stroke={isSel ? "#ffe6c2" : c}
                      strokeWidth={isSel ? 0.75 : 0.45}
                    />
                    {n.deviant && (
                      <text x={n.x} y={n.y * 0.92 + 1.1} textAnchor="middle"
                        fontSize="3" fill={st === "taken" ? "#0a0605" : GM.rose}>◈</text>
                    )}
                    {n.locked && (
                      <text x={n.x} y={n.y * 0.92 + 1.2} textAnchor="middle"
                        fontSize="3.2" fill="#8a7a9a">⌘</text>
                    )}
                  </g>
                );
              })}
            </svg>
            <div style={{
              position: "absolute", bottom: 8, left: 0, right: 0, textAlign: "center",
              fontSize: 9, letterSpacing: 3, color: GM.vellumFaint, pointerEvents: "none",
            }}>TAP A STAR</div>
          </div>

          {/* ── the reading ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{
              padding: "16px 18px", borderRadius: 12,
              background: "linear-gradient(175deg, #1c1009, #0d0705)",
              border: `1px solid ${GM.pageBorder}`, minHeight: 190,
              animation: "cnIn 0.5s ease-out 0.06s backwards",
            }}>
              {!selNode ? (
                <div style={{ fontFamily: GM.fontBody, fontSize: 16, lineHeight: 1.6, color: GM.vellumDim }}>
                  One coin now. The same {GLYPH.kd} that buys a weapon buys a star —
                  {" "}{RULES.ASCEND_COST} of it per Ascension point — so every star
                  you light is something you did not carry, and every
                  weapon you carry is a star you did not light.
                  <br /><br />
                  Five ranks make a <b>level</b>. Three levels is the ceiling of
                  everything in this Empire, including the loudest thing you
                  will ever say. A Treasure's ranks pour into the same pool as
                  a star's — which is how a good weapon carries you most of the
                  way to a level you never paid for, and why taking it off
                  takes that away again.
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: GM.font, fontSize: 15, letterSpacing: 2, color: GM.vellum }}>
                    {selNode.name}
                  </div>
                  <div style={{
                    fontSize: 10, letterSpacing: 2, marginTop: 4, marginBottom: 10,
                    color: selNode.deviant ? GM.rose : GM.vellumFaint,
                  }}>
                    {selNode.locked ? "SEALED" : selNode.cost === 0 ? "FREE" : `${selNode.cost} POINT${selNode.cost > 1 ? "S" : ""}`}
                    {selNode.deviant ? " · THE FORK" : ""}
                    {selNode.impera ? " · IMPERA" : ""}
                  </div>
                  {/* THE COPY LAW: mechanics in the bright text,
                      flavour underneath in italic. A player skimming
                      for "what does this do" never has to read past
                      the first block, and a player who wants the
                      world gets it without the two being tangled. */}
                  <div style={{
                    fontFamily: GM.fontBody, fontSize: 15, lineHeight: 1.6,
                    color: selNode.deviant ? "#f0b0a8" : GM.vellum,
                  }}>{selNode.desc}</div>
                  {selNode.lore && (
                    <div style={{
                      fontFamily: GM.fontBody, fontSize: 13, lineHeight: 1.55,
                      color: GM.vellumDim, fontStyle: "italic", marginTop: 8,
                    }}>{selNode.lore}</div>
                  )}
                  {selNode.track && TRACKS[selNode.track] && (() => {
                    const pr = deriveTrack(soul, selNode.track);
                    const after = trackLevelOf(pr.ranks + (selNode.fx?.n || 0));
                    return (
                      <div style={{ marginTop: 10, fontSize: 11, letterSpacing: 1.5, color: GM.amethystBright }}>
                        {pr.ranks} → {pr.ranks + (selNode.fx?.n || 0)} RANKS
                        {after > pr.level
                          ? ` · REACHES LEVEL ${after} · ${TRACKS[selNode.track].unit}`
                          : ` · LEVEL ${pr.level} → ${RULES.RANKS_PER_LEVEL * (pr.level + 1) - (pr.ranks + (selNode.fx?.n || 0))} SHORT OF LEVEL ${pr.level + 1}`}
                      </div>
                    );
                  })()}

                  {selNode.locked && (
                    <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: "#a08ab0" }}>
                      {UHLTI.icon} <strong>{UHLTI.name}</strong> — {UHLTI.desc}
                    </div>
                  )}

                  {selStatus === "open" && !confirmDeviant && (
                    <button onClick={() => take(selNode)} style={{
                      width: "100%", marginTop: 16, padding: "12px 0", borderRadius: 10, cursor: "pointer",
                      background: selNode.deviant
                        ? "linear-gradient(175deg, #8e3a30, #4a1a14)"
                        : "linear-gradient(175deg, #a8601e, #4a2810)",
                      border: `1px solid ${selNode.deviant ? GM.rose : GM.inkGold}`,
                      color: "#ffe6c2", fontFamily: GM.font, fontSize: 13, letterSpacing: 4,
                    }}>{selNode.deviant ? "TAKE THE FREE THING" : "LIGHT IT"}</button>
                  )}

                  {confirmDeviant && selNode.deviant && (
                    <div style={{
                      marginTop: 14, padding: "12px 14px", borderRadius: 10,
                      background: "rgba(142,58,48,0.14)", border: "1px solid #8e3a30",
                      animation: "cnWarn 1.4s ease-in-out infinite",
                    }}>
                      <div style={{ fontSize: 12, lineHeight: 1.55, color: "#f0b0a8", marginBottom: 12 }}>
                        Your Max Chao is <strong>{deriveMaxChao(soul)}</strong>. Take this and it is
                        {" "}<strong>{deriveMaxChao(soul)}</strong> for the rest of this Soul's life,
                        no matter how well you fight. There is no undo, no respec, no later.
                      </div>
                      <div style={{ display: "flex", gap: 9 }}>
                        <button onClick={() => take(selNode)} style={{
                          flex: 1, padding: "10px 0", borderRadius: 8, cursor: "pointer",
                          background: "linear-gradient(175deg, #8e3a30, #4a1a14)",
                          border: `1px solid ${GM.rose}`, color: "#ffdad4",
                          fontFamily: GM.font, fontSize: 12, letterSpacing: 2,
                        }}>I UNDERSTAND</button>
                        <button onClick={() => setConfirmDeviant(false)} style={{
                          flex: 1, padding: "10px 0", borderRadius: 8, cursor: "pointer",
                          background: "linear-gradient(175deg, #1c1009, #0d0705)",
                          border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
                          fontFamily: GM.font, fontSize: 12, letterSpacing: 2,
                        }}>WALK AWAY</button>
                      </div>
                    </div>
                  )}

                  {selStatus === "taken" && (
                    <div style={{ marginTop: 14, fontSize: 11, letterSpacing: 3, color: GM.amethystBright }}>★ LIT</div>
                  )}
                  {selStatus === "unaffordable" && (
                    <div style={{ marginTop: 14, fontSize: 11, letterSpacing: 2, color: GM.vellumFaint }}>
                      Not enough Ascension points. Buy one with {GLYPH.kd}, or walk the road again.
                    </div>
                  )}
                  {selStatus === "unreachable" && !selNode.locked && (
                    <div style={{ marginTop: 14, fontSize: 11, letterSpacing: 2, color: GM.vellumFaint }}>
                      {soul?.deviant && selNode.deviant ? "Already taken. Forever." : "Light the star before it first."}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── the reading of you ── */}
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: "linear-gradient(175deg, #150c07, #0a0605)",
              border: `1px solid ${GM.pageBorder}`,
              animation: "cnIn 0.5s ease-out 0.12s backwards",
            }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: GM.inkGoldBright, marginBottom: 10 }}>
                {(sp?.name || "BEB").toUpperCase()} · {deriveStanding(soul).toUpperCase()}
              </div>
              {/* Every row is now interrogable. `k` comes off the
                  note so the label and its explanation can never
                  drift apart — one edit in perryData moves both. */}
              {(() => {
                const R = (id, v, c) => {
                  const n = STAT_NOTES[id];
                  return (
                    <Row
                      k={n.key} v={v} c={c} note={n}
                      open={openStat === id}
                      onClick={() => setOpenStat(openStat === id ? null : id)}
                    />
                  );
                };
                return (
                  <>
                    {R("anima", deriveAnimaCap(soul), GM.rose)}
                    {R("chao", `${deriveMaxChao(soul)}${soul?.deviant ? " · LOCKED" : ""}`, GM.amethystBright)}
                    {R("lyt", deriveLyt(soul), GM.inkGoldBright)}
                    {R("lyf", deriveLyf(soul), "#7ce8d8")}
                    {R("winded", `${(deriveExhaustMs(soul) / 1000).toFixed(1)}s`, GM.vellumDim)}
                    {R("stars", `${Object.keys(taken).length} · ${spentPoints(soul)} spent`, GM.vellumDim)}
                  </>
                );
              })()}

              {/* ══════════════════════════════════════════════
                  THE RANK LADDER — 2026-07-29
                  ══════════════════════════════════════════════
                  Six tracks, one pip bar each. This panel exists
                  because the old constellation could tell you a
                  star was lit and could NOT tell you how far along
                  anything was — caP'n asked for exactly this:
                  "maybe you can see the rank up levels on stuff
                  like that to make it easier for the player."

                  The gear share is printed separately and in a
                  different colour, because "3 of your 5 ranks are
                  coming off the axe you are holding" is the single
                  most useful sentence this screen can say. */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 7 }}>
                  RANKS · {RULES.RANKS_PER_LEVEL} PER LEVEL · {RULES.LEVEL_CAP} LEVELS MAX
                </div>
                {TRACK_ORDER.map((key) => {
                  const t = TRACKS[key];
                  const pr = deriveTrack(soul, key);
                  // what the sky gave vs what the paws are holding
                  const fromStars = NODES
                    .filter((n) => taken[n.id] && n.fx?.rank === key)
                    .reduce((a, n) => a + (n.fx.n || 0), 0);
                  const fromGear = Math.max(0, pr.ranks - fromStars);
                  const isOpen = openTrack === key;
                  return (
                    <div key={key} style={{ marginBottom: 7 }}>
                      <div
                        onClick={() => setOpenTrack(isOpen ? null : key)}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 10, letterSpacing: 1, cursor: "pointer" }}
                      >
                        <span style={{ color: pr.level > 0 ? t.tint : GM.vellumFaint }}>
                          {t.glyph} {t.name.toUpperCase()}
                        </span>
                        <span style={{ color: pr.maxed ? GM.inkGoldBright : GM.vellumFaint, fontSize: 9 }}>
                          {pr.maxed ? "LEVEL 3 · MAX" : `LEVEL ${pr.level}`}
                          {fromGear > 0 && <span style={{ color: GM.inkGoldBright }}> · +{fromGear} HELD</span>}
                        </span>
                      </div>
                      {/* fifteen pips, three groups of five — the
                          level boundaries are visible in the gaps
                          rather than drawn, which costs nothing and
                          reads instantly. */}
                      <div style={{ display: "flex", gap: 2, marginTop: 3 }}>
                        {Array.from({ length: RULES.RANKS_PER_LEVEL * RULES.LEVEL_CAP }, (_, i) => {
                          const filled = i < pr.ranks;
                          const held = filled && i >= fromStars;
                          return (
                            <div key={i} style={{
                              flex: 1, height: 4, borderRadius: 2,
                              marginRight: (i + 1) % RULES.RANKS_PER_LEVEL === 0 ? 5 : 0,
                              background: filled ? (held ? GM.inkGoldBright : t.tint) : "rgba(255,255,255,0.07)",
                              boxShadow: filled ? `0 0 6px ${held ? GM.inkGoldBright : t.tint}` : "none",
                              transition: "background 0.2s",
                            }} />
                          );
                        })}
                      </div>
                      {!pr.maxed && (
                        <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 2 }}>
                          {pr.toNext} more → LEVEL {pr.level + 1} · {t.unit}
                        </div>
                      )}
                      {/* ── WHAT THIS TRACK ACTUALLY DOES ──
                          `plain` first and always. `lore` after it,
                          never instead of it. `also` last, because
                          a cross-reference is only useful once you
                          already understand the thing. */}
                      {isOpen && (
                        <div style={{
                          margin: "7px 0 10px", padding: "9px 11px", borderRadius: 8,
                          background: "rgba(0,0,0,0.32)", border: `1px solid ${GM.pageBorder}`,
                          fontFamily: GM.fontBody, fontSize: 13, lineHeight: 1.55,
                          animation: "cnIn 0.22s ease-out",
                        }}>
                          <div style={{ color: GM.vellum }}>{t.plain}</div>
                          <div style={{ marginTop: 7, color: GM.vellumDim, fontStyle: "italic" }}>{t.lore}</div>
                          <div style={{ marginTop: 8, fontSize: 10, letterSpacing: 1.5, color: GM.amethystBright }}>
                            TOUCHES: {String(t.also).toUpperCase()}
                          </div>
                          <div style={{ marginTop: 5, fontSize: 10, letterSpacing: 1.5, color: GM.vellumFaint }}>
                            {pr.ranks} / {pr.cap} RANKS · {RULES.RANKS_PER_LEVEL} PER LEVEL
                            {fromGear > 0 && ` · ${fromGear} FROM GEAR (LOST IF UNEQUIPPED)`}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* ── WHAT YOU ARE ACTUALLY CARRYING ──
                  It reads the SLOTS, not the stars, because after
                  this pass those are two different facts and the
                  gap between them is the point of the Grimoire.
                  Owning nine powers and carrying none is a real
                  and completely legal state, and this line has to
                  be able to say so. */}
              {deriveRegalia(soul).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 4 }}>
                    CARRYING {deriveSlots(soul).filter(Boolean).length} / {deriveRegalia(soul).length} KNOWN
                  </div>
                  <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.7 }}>
                    {deriveSlots(soul).map((id, i) => {
                      const p = id ? REGALIA[id] : null;
                      return (
                        <span key={i} style={{ marginRight: 10, whiteSpace: "nowrap" }}>
                          <span style={{ color: p ? GM.inkGoldBright : "#3a2a20" }}>[{slotKey(i)}]</span>
                          {" "}
                          <span style={{ color: p ? GM.vellum : "#3a2a20" }}>
                            {p ? `${p.icon} ${p.name}` : "—"}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
        )}

        <button onClick={onBack} style={{
          width: "min(940px, 96vw)", marginTop: 16,
          padding: "12px 0", borderRadius: 10, cursor: "pointer",
          background: "linear-gradient(175deg, #1c1009, #0d0705)",
          border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
          fontFamily: GM.font, fontSize: 12, letterSpacing: 4,
        }}>← BACK</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// THE GRIMOIRE — what you carry into the fire
// ════════════════════════════════════════════════════════════
// caP'n, 2026-07-28: "just cause Perry is a Beb doesn't mean he
// can't access immense power, time for him to power up."
//
// TEN POWERS. FIVE KEYS. That gap is the whole screen, and it is
// deliberate. Both of Perry's paws live on S D F J K L during a
// fight — every key outside that grid is a hand LEAVING the guard,
// paid for in real milliseconds — so the ceiling was never "how
// many powers can we fit," it was "how many can a beb reach
// without dying for it." Five reaches. (RULES.SLOTS. A dial.)
//
// It costs nothing, it is instant, and it is reversible forever.
// A loadout is a preference, not a purchase, and charging for one
// would be beneath us in exactly the same way charging for the
// colour picker would be.
//
// THE INTERACTION, in one sentence: click a slot to select it,
// click a power to put it there. Click a filled slot twice to
// empty it. No drag, no modal, no confirm — this is a screen
// caP'n will open forty times a day while testing.
// ════════════════════════════════════════════════════════════
function Grimoire({ soul, onSlots }) {
  const [sel, setSel] = useState(0);
  const slots = deriveSlots(soul);
  const bench = deriveBenched(soul);
  const known = deriveRegalia(soul);
  const theme = themeFor(soul?.species);

  // Every mutation goes through here and hands the WHOLE rack up.
  // One leaf write, one shape, no partial states — a rack that is
  // written a slot at a time is a rack that can be half-saved when
  // a tab closes.
  const put = (index, id) => {
    if (!onSlots) return;
    const next = slots.slice();
    // A power lives in exactly one slot. Moving it into a second
    // one takes it out of the first rather than cloning it, which
    // is the only behaviour that can't produce two keys that both
    // claim the same Chao.
    if (id) {
      const prior = next.indexOf(id);
      if (prior >= 0) next[prior] = null;
    }
    next[index] = id;
    onSlots(next);
  };

  const RUNG = {
    impera:   { label: "IMPERA",   c: GM.amethystBright },
    uhlti:    { label: "U'HLTI",   c: "#9fe8ff" },
    emperium: { label: "EMPERIUM", c: theme.tint },
  };

  const costLine = (p) => {
    const bits = [];
    if (p.costChao) bits.push(`${p.costChao} CHAO`);
    if (p.costAnima) bits.push(`${p.costAnima} ANIMA`);
    if (p.ms) bits.push(`${Math.round(p.ms / 1000)}s`);
    if (p.parries) bits.push(`${p.parries} ANSWERS`);
    return bits.join(" · ") || "FREE";
  };

  return (
    <div style={{
      width: "min(940px, 96vw)", display: "grid", gap: 16,
      gridTemplateColumns: "minmax(300px, 1.25fr) minmax(260px, 1fr)",
      animation: "cnIn 0.4s ease-out",
    }}>
      {/* ── THE RACK ── */}
      <div style={{
        padding: "16px 18px", borderRadius: 14,
        background: "linear-gradient(175deg, #1c1009, #0d0705)",
        border: `1px solid ${GM.pageBorder}`,
      }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: GM.vellumFaint, marginBottom: 4 }}>
          WHAT YOU CARRY
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.55, color: GM.vellumFaint, marginBottom: 12 }}>
          Five slots, and they are keys <span style={{ color: GM.inkGoldBright }}>1</span> through{" "}
          <span style={{ color: GM.inkGoldBright }}>{RULES.SLOTS}</span> on the road. Both your paws
          live on the guard during a fight — reaching off it costs you real time, so you carry five
          and you leave the rest at home. Pick a slot, then pick what goes in it.
        </div>

        {slots.map((id, i) => {
          const p = id ? REGALIA[id] : null;
          const on = sel === i;
          const r = p ? RUNG[p.rung] || RUNG.impera : null;
          return (
            <div
              key={i}
              onClick={() => (on && p ? put(i, null) : setSel(i))}
              style={{
                padding: "10px 12px", marginBottom: 8, borderRadius: 10, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 11,
                background: on ? "rgba(240,152,74,0.10)" : "rgba(0,0,0,0.3)",
                border: `1px solid ${on ? GM.inkGold : p ? GM.pageBorder : "#241a14"}`,
                transition: "all 0.15s ease",
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700,
                border: `1px solid ${on ? GM.inkGold : GM.inkGoldDim}`,
                color: on ? "#ffe6c2" : GM.inkGoldBright,
                background: "linear-gradient(175deg, #2a1608, #150c07)",
              }}>{slotKey(i)}</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {p ? (
                  <>
                    <div style={{ fontSize: 13, color: GM.vellum, letterSpacing: 1 }}>
                      {p.icon} {p.name}
                      <span style={{
                        marginLeft: 7, fontSize: 8, letterSpacing: 1.5,
                        padding: "1px 5px", borderRadius: 4,
                        border: `1px solid ${r.c}66`, color: r.c,
                      }}>{r.label}</span>
                    </div>
                    <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1, marginTop: 3 }}>
                      {costLine(p)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#4a3a30", letterSpacing: 2 }}>
                    EMPTY — nothing answers to this key
                  </div>
                )}
              </div>

              {on && (
                <div style={{ fontSize: 9, letterSpacing: 2, color: GM.inkGoldBright, whiteSpace: "nowrap" }}>
                  {p ? "CLICK TO EMPTY" : "SELECTED"}
                </div>
              )}
            </div>
          );
        })}

        {known.length === 0 && (
          <div style={{
            marginTop: 10, padding: "12px 14px", borderRadius: 10,
            border: `1px solid ${GM.pageBorder}`, background: "rgba(0,0,0,0.3)",
            fontSize: 12, lineHeight: 1.6, color: GM.vellumFaint,
          }}>
            You do not know any powers yet. Every one of them is a star —
            <span style={{ color: GM.inkGoldBright }}> ✦ CONSTELLATION </span>
            is where they are lit, and Renown is what lights them. Walk the road. It pays.
          </div>
        )}
      </div>

      {/* ── THE BENCH ── */}
      <div style={{
        padding: "16px 18px", borderRadius: 14, alignSelf: "start",
        background: "linear-gradient(175deg, #1c1009, #0d0705)",
        border: `1px solid ${GM.pageBorder}`,
      }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: GM.vellumFaint, marginBottom: 4 }}>
          WHAT YOU KNOW
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.55, color: GM.vellumFaint, marginBottom: 12 }}>
          {bench.length === 0 && known.length > 0
            ? "Everything you know is on the rack. There is nothing to leave behind yet — which will stop being true the moment you light one more star."
            : `Click one to put it in slot ${slotKey(sel)}. Nothing here is lost by being left at home.`}
        </div>

        {bench.map((id) => {
          const p = REGALIA[id];
          if (!p) return null;
          const r = RUNG[p.rung] || RUNG.impera;
          return (
            <div
              key={id}
              onClick={() => put(sel, id)}
              style={{
                padding: "10px 12px", marginBottom: 8, borderRadius: 10, cursor: "pointer",
                background: "rgba(0,0,0,0.3)",
                border: `1px solid ${GM.pageBorder}`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13, color: GM.vellum, letterSpacing: 1 }}>
                  {p.icon} {p.name}
                </div>
                <span style={{ fontSize: 8, letterSpacing: 1.5, color: r.c, whiteSpace: "nowrap" }}>
                  {r.label}
                </span>
              </div>
              <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1, marginTop: 3 }}>
                {costLine(p)}
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 5 }}>
                {p.desc}
              </div>
            </div>
          );
        })}

        {/* The one line of doctrine this screen is allowed. It is
            here because a player who has just been handed ten
            powers in a parrying game deserves to be told, once,
            that the powers are not the game. */}
        <div style={{ marginTop: 14, fontSize: 11, lineHeight: 1.6, color: GM.vellumFaint }}>
          <span style={{ color: GM.inkGoldBright }}>Nothing here answers a fight for you.</span>{" "}
          Every one of them buys a beat, a bite, or one mistake — and every one of them is breath
          you did not keep. Spend a single Chao against a foe and the Growth Law does not speak to
          you that fight. It has always been that trade. There is just more on the other side of
          it now.
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// THE ARMOURY
// What you are holding while you know all of that. Owned
// Treasures on the left, the finish it burns with on the right.
// Equipping is a leaf write and instant; finishes are free and
// always all unlocked, because the first thing anyone touches in
// any game ever made is the colour picker and charging for it is
// beneath us.
// ════════════════════════════════════════════════════════════
function Armoury({ soul, onEquip, onFinish }) {
  const held = deriveTreasure(soul);
  const fin = deriveFinish(soul);

  return (
    <div style={{
      width: "min(940px, 96vw)", display: "grid", gap: 16,
      gridTemplateColumns: "minmax(300px, 1.5fr) minmax(220px, 1fr)",
      animation: "cnIn 0.4s ease-out",
    }}>
      {/* ── what is in his paws ── */}
      <div style={{
        padding: "16px 18px", borderRadius: 14,
        background: "linear-gradient(175deg, #1c1009, #0d0705)",
        border: `1px solid ${GM.pageBorder}`,
      }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: GM.vellumFaint, marginBottom: 12 }}>
          WHAT YOU CARRY
        </div>
        {TREASURE_ORDER.map((id) => {
          const t = TREASURES[id];
          const owns = ownsTreasure(soul, id);
          const on = held.id === id;
          return (
            <div
              key={id}
              onClick={() => owns && !on && onEquip && onEquip(id)}
              style={{
                padding: "11px 13px", marginBottom: 8, borderRadius: 10,
                cursor: owns && !on ? "pointer" : "default",
                background: on ? "rgba(240,152,74,0.10)" : "rgba(0,0,0,0.3)",
                border: `1px solid ${on ? GM.inkGold : owns ? GM.pageBorder : "#241a14"}`,
                opacity: owns ? 1 : 0.42,
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13, color: on ? "#ffe6c2" : GM.vellum, letterSpacing: 1 }}>
                  {t.icon} {t.name}
                </div>
                <span style={{ fontSize: 9, letterSpacing: 2, color: on ? GM.inkGoldBright : GM.vellumFaint }}>
                  {on ? "IN YOUR PAWS" : owns ? "EQUIP"
                    : t.price?.dc != null ? `WHYLLIE · ${GLYPH.dc} ${t.price.dc}`
                      : `WHYLLIE · ${GLYPH.kd} ${t.price?.kindling || 0}`}
                </span>
              </div>
              <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1, marginTop: 5 }}>
                {treasureLine(t)}
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 5 }}>
                {t.desc}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── the finish ── */}
      <div style={{
        padding: "16px 18px", borderRadius: 14, alignSelf: "start",
        background: "linear-gradient(175deg, #1c1009, #0d0705)",
        border: `1px solid ${GM.pageBorder}`,
      }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: GM.vellumFaint, marginBottom: 4 }}>
          THE FINISH
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginBottom: 12 }}>
          The ember it burns with and the trail it leaves behind it. Costs nothing. Changes nothing. Everyone will change it anyway.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {FINISH_ORDER.map((id) => {
            const f = FINISHES[id];
            const on = fin.id === id;
            return (
              <div
                key={id}
                onClick={() => onFinish && onFinish(id)}
                style={{
                  padding: "9px 10px", borderRadius: 9, cursor: "pointer",
                  background: "rgba(0,0,0,0.35)",
                  border: `1px solid ${on ? f.tint : GM.pageBorder}`,
                  boxShadow: on ? `0 0 14px ${f.tint}55` : "none",
                  textAlign: "center",
                }}
              >
                <div style={{
                  width: "100%", height: 6, borderRadius: 3, marginBottom: 6,
                  background: `linear-gradient(90deg, ${f.trail}, ${f.tint}, ${f.hot})`,
                  boxShadow: `0 0 10px ${f.tint}`,
                }} />
                <div style={{ fontSize: 10, letterSpacing: 2, color: on ? f.hot : GM.vellumDim }}>
                  {f.name.toUpperCase()}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 16, fontSize: 11, lineHeight: 1.6, color: GM.vellumFaint }}>
          <span style={{ color: GM.inkGoldBright }}>The grid:</span> left column is your left hand,
          right column your right, and each row is the same finger on both — F J index, D K middle,
          S L ring. Each key owns a colour, and the row is the family: HIGH warm, MID cold, LOW bright.
          Nothing on screen will ever tell you which one is coming. The creature will.
        </div>
      </div>
    </div>
  );
}

// ── A NUMBER THE PLAYER CAN INTERROGATE, 2026-07-29 ──────────
// caP'n: "a tiny description too when you click on the sections
//  like 'Lyf - Breath - 2' should have a quick sentence explaining
//  what that is."
//
// A row with a `note` opens; a row without one behaves exactly as
// it always did, so nothing has to be explained that doesn't have
// an explanation written for it yet. The ⌄ only appears when there
// is something behind it — an affordance that lies is worse than
// no affordance.
function Row({ k, v, c, note, open, onClick }) {
  const can = !!note;
  return (
    <div style={{ marginBottom: 5 }}>
      <div
        onClick={can ? onClick : undefined}
        style={{
          display: "flex", justifyContent: "space-between", fontSize: 12,
          cursor: can ? "pointer" : "default",
          opacity: can && open ? 1 : undefined,
        }}
      >
        <span style={{ color: open ? GM.inkGoldBright : GM.vellumFaint, letterSpacing: 2 }}>
          {k}{can && <span style={{ opacity: 0.45, marginLeft: 5, fontSize: 9 }}>{open ? "⌃" : "⌄"}</span>}
        </span>
        <span style={{ color: c, letterSpacing: 1 }}>{v}</span>
      </div>
      {can && open && (
        <div style={{
          margin: "6px 0 9px", padding: "9px 11px", borderRadius: 8,
          background: "rgba(0,0,0,0.32)", border: `1px solid ${GM.pageBorder}`,
          fontFamily: GM.fontBody, fontSize: 13, lineHeight: 1.55, color: GM.vellumDim,
          animation: "cnIn 0.22s ease-out",
        }}>
          <div style={{ color: GM.vellum }}>{note.what}</div>
          {note.more && <div style={{ marginTop: 7 }}>{note.more}</div>}
          {note.track && TRACKS[note.track] && (
            <div style={{ marginTop: 8, fontSize: 10, letterSpacing: 1.5, color: GM.amethystBright }}>
              RAISED BY: {TRACKS[note.track].glyph} {TRACKS[note.track].name.toUpperCase()}
            </div>
          )}
          {!note.track && (
            <div style={{ marginTop: 8, fontSize: 10, letterSpacing: 1.5, color: GM.vellumFaint }}>
              NO STAR RAISES THIS
            </div>
          )}
        </div>
      )}
    </div>
  );
}
